import {
    AmbientLightConfig,
    DirectionalLightConfig,
    LightConfig,
    PointLightConfig,
    SpotLightConfig,
} from "@/3d/LightConfig";
import { Button } from "@/dom/components/Button/Button";
import { Dropdown } from "@/dom/components/Dropdown/Dropdown";
import { Input, InputNumber } from "@/dom/components/Input/Input";
import { Column } from "@/dom/components/Panel/Column";
import { Row } from "@/dom/components/Panel/Row";
import { Switch } from "@/dom/components/Switch/Switch";
import { useDemoState } from "@/state/demoState";
import React from "react";

const LightTypeOptions: { label: string; value: LightConfig["type"] }[] = [
    {
        label: "Point",
        value: "point",
    },
    {
        label: "Directional",
        value: "directional",
    },
    {
        label: "Spot",
        value: "spot",
    },
    {
        label: "Ambient",
        value: "ambient",
    },
];

export const LightSetup: React.FC = () => {
    const { lightConfig, setLightConfig } = useDemoState();

    const handleLightTypeChange = (type: LightConfig["type"], index: number) => {
        setLightConfig([
            ...lightConfig.slice(0, index),
            getDefaultLightOfType(type),
            ...lightConfig.slice(index + 1),
        ]);
    };

    const handleLightConfigChange = (config: LightConfig, index: number) => {
        setLightConfig([...lightConfig.slice(0, index), config, ...lightConfig.slice(index + 1)]);
    };

    const handleRemoveLight = (index: number) => {
        setLightConfig(lightConfig.filter((_, i) => i !== index));
    };

    const handleAddLight = () => {
        setLightConfig([...lightConfig, getDefaultLightOfType("point")]);
    };

    const handleExportLightConfig = () => {
        const json = JSON.stringify(lightConfig);
        const blob = new Blob([json], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "lightConfig.json";
        a.click();
    };

    return (
        <Column>
            {lightConfig.map((config, index) => (
                <Column key={config.type}>
                    <Row>
                        <span>#{index + 1}</span>{" "}
                        <Button onClick={() => handleRemoveLight(index)}>Удалить</Button>
                    </Row>
                    <Dropdown
                        options={LightTypeOptions}
                        value={config.type}
                        onChange={(value) => handleLightTypeChange(value, index)}
                    />
                    {config.type === "ambient" && (
                        <AmbientLightSetup
                            lightConfig={config}
                            setLightConfig={(config) => handleLightConfigChange(config, index)}
                        />
                    )}
                    {config.type === "point" && (
                        <PointLightSetup
                            lightConfig={config}
                            setLightConfig={(config) => handleLightConfigChange(config, index)}
                        />
                    )}
                    {config.type === "directional" && (
                        <DirectionalLightSetup
                            lightConfig={config}
                            setLightConfig={(config) => handleLightConfigChange(config, index)}
                        />
                    )}
                    {config.type === "spot" && (
                        <SpotLightSetup
                            lightConfig={config}
                            setLightConfig={(config) => handleLightConfigChange(config, index)}
                        />
                    )}

                    <hr />
                </Column>
            ))}
            <Button onClick={handleAddLight}>Добавить</Button>
            <Button onClick={handleExportLightConfig}>Экспортировать</Button>
        </Column>
    );
};

type LightSetupProps<TLight extends LightConfig> = {
    lightConfig: TLight;
    setLightConfig: (lightConfig: TLight) => void;
};

const PointLightSetup: React.FC<LightSetupProps<PointLightConfig>> = ({
    lightConfig,
    setLightConfig,
}) => {
    return (
        <Column>
            <Row>Положение (x, y, z)</Row>
            <PositionSetup
                position={lightConfig.position}
                setPosition={(position) => setLightConfig({ ...lightConfig, position })}
            />
            <Row>Интенсивность</Row>
            <Row>
                <InputNumber
                    value={lightConfig.intensity}
                    onBlur={(value) => setLightConfig({ ...lightConfig, intensity: value })}
                />
            </Row>
            <Row>Расстояние</Row>
            <Row>
                <InputNumber
                    value={lightConfig.distance}
                    onBlur={(value) => setLightConfig({ ...lightConfig, distance: value })}
                />
            </Row>
            <Row>Цвет</Row>
            <Row>
                <Input
                    value={lightConfig.color ?? "#ffffff"}
                    onChange={(e) => setLightConfig({ ...lightConfig, color: e.target.value })}
                />
            </Row>
            <Row>Тень</Row>
            <Row>
                <Switch
                    checked={lightConfig.castShadow ?? false}
                    onChange={(value) => setLightConfig({ ...lightConfig, castShadow: value })}
                />
            </Row>
        </Column>
    );
};

const AmbientLightSetup: React.FC<LightSetupProps<AmbientLightConfig>> = ({
    lightConfig,
    setLightConfig,
}) => {
    return (
        <Column>
            <Row>Интенсивность</Row>
            <Row>
                <InputNumber
                    value={lightConfig.intensity}
                    onBlur={(value) => setLightConfig({ ...lightConfig, intensity: value })}
                />
            </Row>
            <Row>Цвет</Row>
            <Row>
                <Input
                    value={lightConfig.color ?? "#ffffff"}
                    onChange={(e) => setLightConfig({ ...lightConfig, color: e.target.value })}
                />
            </Row>
            <Row>Положение (x, y, z)</Row>
            <PositionSetup
                position={lightConfig.position}
                setPosition={(position) => setLightConfig({ ...lightConfig, position })}
            />
        </Column>
    );
};

const DirectionalLightSetup: React.FC<LightSetupProps<DirectionalLightConfig>> = ({
    lightConfig,
    setLightConfig,
}) => {
    return (
        <Column>
            <Row>Интенсивность</Row>
            <Row>
                <InputNumber
                    value={lightConfig.intensity}
                    onBlur={(value) => setLightConfig({ ...lightConfig, intensity: value })}
                />
            </Row>
            <Row>Цвет</Row>
            <Row>
                <Input
                    value={lightConfig.color ?? "#ffffff"}
                    onChange={(e) => setLightConfig({ ...lightConfig, color: e.target.value })}
                />
            </Row>
            <Row>Положение (x, y, z)</Row>
            <PositionSetup
                position={lightConfig.position}
                setPosition={(position) => setLightConfig({ ...lightConfig, position })}
            />
            <Row>Цель (x, y, z)</Row>
            <PositionSetup
                position={lightConfig.target}
                setPosition={(position) => setLightConfig({ ...lightConfig, target: position })}
            />
            <Row>Тень</Row>
            <Row>
                <Switch
                    checked={lightConfig.castShadow ?? false}
                    onChange={(value) => setLightConfig({ ...lightConfig, castShadow: value })}
                />
            </Row>
        </Column>
    );
};

const SpotLightSetup: React.FC<LightSetupProps<SpotLightConfig>> = ({
    lightConfig,
    setLightConfig,
}) => {
    return (
        <Column>
            <Row>Интенсивность</Row>
            <Row>
                <InputNumber
                    value={lightConfig.intensity}
                    onBlur={(value) => setLightConfig({ ...lightConfig, intensity: value })}
                />
            </Row>
            <Row>Цвет</Row>
            <Row>
                <Input
                    value={lightConfig.color ?? "#ffffff"}
                    onChange={(e) => setLightConfig({ ...lightConfig, color: e.target.value })}
                />
            </Row>
            <Row>Положение (x, y, z)</Row>
            <PositionSetup
                position={lightConfig.position}
                setPosition={(position) => setLightConfig({ ...lightConfig, position })}
            />
            <Row>Цель (x, y, z)</Row>
            <PositionSetup
                position={lightConfig.target}
                setPosition={(position) => setLightConfig({ ...lightConfig, target: position })}
            />
            <Row>Тень</Row>
            <Row>
                <Switch
                    checked={lightConfig.castShadow ?? false}
                    onChange={(value) => setLightConfig({ ...lightConfig, castShadow: value })}
                />
            </Row>
            <Row>Угол</Row>
            <Row>
                <InputNumber
                    value={lightConfig.angle}
                    onBlur={(value) => setLightConfig({ ...lightConfig, angle: value })}
                />
            </Row>
            <Row>Penumbra</Row>
            <Row>
                <InputNumber
                    value={lightConfig.penumbra}
                    onBlur={(value) => setLightConfig({ ...lightConfig, penumbra: value })}
                />
            </Row>
            <Row>Расстояние</Row>
            <Row>
                <InputNumber
                    value={lightConfig.distance}
                    onBlur={(value) => setLightConfig({ ...lightConfig, distance: value })}
                />
            </Row>
        </Column>
    );
};
const PositionSetup: React.FC<{
    position: [number, number, number];
    setPosition: (position: [number, number, number]) => void;
}> = ({ position, setPosition }) => {
    return (
        <Row>
            <InputNumber
                width="small"
                value={position[0]}
                onBlur={(value) => setPosition([value, position[1], position[2]])}
            />
            <InputNumber
                width="small"
                value={position[1]}
                onBlur={(value) => setPosition([position[0], value, position[2]])}
            />
            <InputNumber
                width="small"
                value={position[2]}
                onBlur={(value) => setPosition([position[0], position[1], value])}
            />
        </Row>
    );
};

const getDefaultLightOfType = (type: LightConfig["type"]): LightConfig => {
    switch (type) {
        case "point":
            return {
                type: "point",
                intensity: 1,
                position: [0, 0, 0],
            };
        case "directional":
            return {
                type: "directional",
                intensity: 1,
                position: [0, 0, 0],
                target: [0, 0, 0],
            };
        case "spot":
            return {
                type: "spot",
                intensity: 1,
                position: [0, 0, 0],
                target: [0, 0, 0],
            };

        case "ambient":
        default:
            return {
                type: "ambient",
                intensity: 1,
                position: [0, 0, 0],
            };
    }
};
